import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtError } from "../api";
import { getCachedMessages, setCachedMessages } from "../magMessages";
import { MapPin, AlertTriangle, Bell, Loader2 } from "lucide-react";
import { LiegenschaftList } from "../components/LiegenschaftList";
import { MeldungenPane } from "../components/MeldungenPane";
import { pushSupported, getPushState, subscribePush, isPWA } from "../push";

const CHECKIN_BASE = "https://asb.whatsdown.cloud/qr";

// Direkte Start-Ansicht: nach der Anmeldung werden alle zugaenglichen
// Standorte ueber /wdobjects geladen und in der WhatsApp-Ansicht gezeigt.
//  /objekte                 -> Liste (keine Auswahl)
//  /objekte/:liegPermId     -> Standort gewaehlt (Meldungen)
export default function Objekte() {
  const navigate = useNavigate();
  const { liegPermId } = useParams();
  const [searchParams] = useSearchParams();
  const initialFilter = searchParams.get("filter") || "";
  const token = sessionStorage.getItem("mag_token");
  const mode = sessionStorage.getItem("mag_mode") || "prod";
  const isTest = mode === "test";

  const [wd, setWd] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pushPrompt, setPushPrompt] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const prefetchedRef = useRef(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/magauth/wdobjects", { headers: { Authorization: `Bearer ${token}` } });
      setWd(data);
    } catch (e) {
      setError(fmtError(e, "Standorte konnten nicht geladen werden"));
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    if (!token) { navigate("/login", { replace: true }); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reiner Hausmeister (keine Schreibrechte irgendwo) -> direkt nach Push fragen.
  // Manager/Admin werden nicht automatisch gefragt (koennen es im Profil aktivieren).
  useEffect(() => {
    if (!wd) return;
    if (wd.user?.role) sessionStorage.setItem("mag_role", wd.user.role);
    const ls = wd.liegenschaften || [];
    const ps = wd.parents || [];
    const hasWrite = ps.some((p) => p.can_create || p.can_manage) || ls.some((l) => l.can_create || l.can_manage);
    if (hasWrite) return;
    // Push nur fuer Hausmeister und nur in der installierten PWA anbieten.
    if (!pushSupported() || !isPWA() || sessionStorage.getItem("push_prompt_seen")) return;
    getPushState().then((st) => { if (st === "default") setPushPrompt(true); });
  }, [wd]);

  // Nach dem Login Meldungen ALLER Standorte vorab laden (oben -> unten),
  // damit die Liste aktuell ist und ein Klick die Meldungen sofort zeigt (Cache-Vorwaermung).
  useEffect(() => {
    if (!wd || !token || prefetchedRef.current) return;
    const ls = (wd.liegenschaften || []).filter((l) => l.perm_id && !l.error);
    if (ls.length === 0) return;
    prefetchedRef.current = true;
    let cancelled = false;
    (async () => {
      const hdr = { headers: { Authorization: `Bearer ${token}` } };
      for (const l of ls) {
        if (cancelled) break;
        try {
          let activity = null;
          try { const { data: act } = await api.get(`/magauth/activity/${l.perm_id}`, hdr); activity = act?.timestamp || null; } catch { /* ignore */ }
          const cached = getCachedMessages(l.perm_id);
          if (cached?.data && activity && cached.activity === activity) continue;
          const { data } = await api.get(`/magauth/messages/${l.perm_id}`, { ...hdr, params: { include_children: 1 } });
          setCachedMessages(l.perm_id, data, activity);
        } catch { /* einzelne Standorte duerfen fehlschlagen */ }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wd]);

  const enablePush = async () => {
    setPushBusy(true);
    try {
      await subscribePush(token);
      toast.success("Benachrichtigungen aktiviert.");
      setPushPrompt(false);
      sessionStorage.setItem("push_prompt_seen", "1");
    } catch (e) {
      toast.error(e?.message || "Benachrichtigungen konnten nicht aktiviert werden.");
    } finally { setPushBusy(false); }
  };

  const dismissPush = () => { setPushPrompt(false); sessionStorage.setItem("push_prompt_seen", "1"); };

  // Standort als gesehen markieren: rote 'neu'-Zahl sofort lokal entfernen + im Backend speichern.
  const markStandortSeen = useCallback(async (permid) => {
    if (!permid) return;
    setWd((prev) => (prev ? { ...prev, liegenschaften: (prev.liegenschaften || []).map((l) => (l.perm_id === permid ? { ...l, new_count: 0 } : l)) } : prev));
    try {
      await api.post(`/magauth/liegenschaft/${permid}/seen`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* nicht kritisch */ }
  }, [token]);

  const liegenschaften = wd?.liegenschaften || [];
  const parents = wd?.parents || [];
  const errorRows = liegenschaften.filter((l) => l.error);
  const canCreateAny = parents.some((p) => p.can_create) || liegenschaften.some((l) => l.can_create);

  const activeRow = liegPermId
    ? (liegenschaften.find((l) => l.perm_id === liegPermId) || { perm_id: liegPermId, title: liegPermId })
    : null;
  const showMessages = !!liegPermId;

  const selectLiegenschaft = (row) => navigate(`/objekte/${row.perm_id}`);

  const reportIssue = (row) => {
    if (!row?.perm_id) return;
    const url = `${CHECKIN_BASE}/${encodeURIComponent(row.perm_id)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const downloadAushang = async (row, size = "a4") => {
    if (!row?.perm_id) return;
    try {
      const { data } = await api.get(`/magauth/liegenschaft/${row.perm_id}/aushang.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { title: row.title || "", size },
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl; a.download = `aushang-${row.perm_id}${size === "a5" ? "-a5" : ""}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
      toast.success(`PDF-Aushang (${size.toUpperCase()}) wird heruntergeladen.`);
    } catch (e) {
      toast.error(fmtError(e, "PDF-Aushang konnte nicht erstellt werden"));
    }
  };

  const createLiegenschaft = async (name, parentPermId) => {
    if (!parentPermId) { toast.error("Kein übergeordnetes Objekt zum Anlegen gefunden."); return; }
    try {
      await api.post(`/magauth/objects/${parentPermId}/child`, { title: name }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Standort „${name}" wurde angelegt.`);
      await load();
    } catch (e) {
      toast.error(fmtError(e, "Standort konnte nicht angelegt werden"));
      throw e;
    }
  };

  return (
    <div className="container-wd py-6" data-testid="objekte-page">
      <div className="flex justify-between items-center mb-3 flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {isTest && (
            <span className="badge-hero" data-testid="objekte-mode"
              style={{ background: "#EFF6FF", borderColor: "#BFDBFE", color: "#1D4ED8" }}>Testsystem</span>
          )}
          {wd && liegenschaften.length > 0 && (
            <span className="text-base font-extrabold truncate" data-testid="objekte-parent-title" style={{ color: "#0F172A", maxWidth: 520 }}>
              {parents.map((p) => p.title).filter(Boolean).join(" · ") || `${wd.count} Standorte`}
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="card-wd p-5 mb-4" style={{ borderColor: "#DC2626" }} data-testid="objekte-error">
          <p className="text-sm font-semibold m-0" style={{ color: "#DC2626" }}>{error}</p>
        </div>
      )}

      {errorRows.length > 0 && (
        <div className="card-wd p-4 mb-4" style={{ borderColor: "#F59E0B" }} data-testid="objekte-permission-errors">
          <p className="text-sm font-bold m-0 mb-1 inline-flex items-center gap-2" style={{ color: "#B45309" }}>
            <AlertTriangle size={15} /> Bei {errorRows.length} Standort(en) konnten die Berechtigungen nicht geladen werden.
          </p>
          <ul className="text-xs m-0 mt-1 pl-5" style={{ color: "#92400E", listStyle: "disc" }}>
            {errorRows.map((l) => (
              <li key={l.perm_id} data-testid={`perm-error-${l.perm_id}`}>{l.title || l.perm_id}: {l.error}</li>
            ))}
          </ul>
        </div>
      )}

      {pushPrompt && (
        <div className="card-wd p-4 mb-4 flex items-center gap-3 flex-wrap" data-testid="push-prompt"
          style={{ borderColor: "#F97316", background: "#FFF7ED" }}>
          <span className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "#F97316" }}>
            <Bell size={18} color="#fff" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold m-0" style={{ color: "#9A3412" }}>Benachrichtigungen aktivieren?</p>
            <p className="text-xs m-0 mt-0.5" style={{ color: "#B45309" }}>Erhalte eine Push-Nachricht, sobald es eine neue Hausmeisteranfrage gibt.</p>
          </div>
          <div className="flex items-center gap-2">
            <button className="btn-secondary" data-testid="push-prompt-dismiss" onClick={dismissPush} style={{ padding: "8px 12px" }}>Später</button>
            <button className="btn-primary" data-testid="push-prompt-enable" onClick={enablePush} disabled={pushBusy} style={{ padding: "8px 14px" }}>
              {pushBusy ? "Aktiviere…" : "Aktivieren"}
            </button>
          </div>
        </div>
      )}

      {wd && liegenschaften.length === 0 && !error ? (
        <div className="card-wd p-10 text-center" data-testid="objekte-empty">
          <MapPin size={34} className="mx-auto mb-3" style={{ color: "#94A3B8" }} />
          <p className="font-extrabold text-lg mb-1" data-testid="objekte-empty-title">Noch keine Standorte</p>
          <p className="text-sm" data-testid="objekte-empty-text" style={{ color: "#64748B", maxWidth: 520, margin: "0 auto" }}>
            Für die E-Mail <strong>{wd.user?.email || ""}</strong> sind noch keine Standorte eingerichtet.
            Bitte wenden Sie sich an den für Sie zuständigen Administrator oder an Makrolog.
          </p>
        </div>
      ) : (
      <div className="card-wd p-0 overflow-hidden flex flex-col"
        style={{ height: "calc(100vh - 210px)", minHeight: 520 }} data-testid="master-detail">
        {!showMessages ? (
          <div className="flex flex-col h-full min-h-0" data-testid="standort-overview">
            {!wd && !error ? (
              <div className="flex items-center justify-center gap-2 p-6 text-sm" style={{ color: "#64748B" }} data-testid="liegenschaften-loading">
                <Loader2 size={16} className="animate-spin" /> Standorte werden geladen…
              </div>
            ) : (
              <LiegenschaftList items={liegenschaften} parents={parents} activePermId={activeRow?.perm_id}
                onSelect={selectLiegenschaft}
                onCreate={canCreateAny ? createLiegenschaft : undefined} onAushang={downloadAushang} />
            )}
          </div>
        ) : (
          <div className="flex flex-col h-full min-h-0" data-testid="standort-detail">
            <MeldungenPane key={activeRow.perm_id} permid={activeRow.perm_id} title={activeRow.title} address={activeRow.address}
              token={token} onBack={() => navigate("/objekte")} onReportIssue={reportIssue} initialFilter={initialFilter} onSeen={markStandortSeen} />
          </div>
        )}
      </div>
      )}

    </div>
  );
}
